import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, MapPin, User, Phone, ShoppingBag, LogOut } from 'lucide-react';
import { useTranslation } from '../Context/TranslationContext';
import { useAuthStore } from '../Zustand/authStore';

const MyProfile = () => {
  const navigate = useNavigate();
  const { isTamil } = useTranslation();
  const { username: storedUsername, clearAuth } = useAuthStore();

  // Simplified card definitions
  const accountCards = [
    {
      id: 'orders',
      icon: Package,
      title: { en: 'My Orders', ta: 'எனது ஆர்டர்கள்' },
      description: { 
        en: 'View your order history',
        ta: 'உங்கள் ஆர்டர் வரலாற்றைக் காண்க'
      },
      link: '/orders'
    },
    {
      id: 'addresses',
      icon: MapPin,
      title: { en: 'My Addresses', ta: 'எனது முகவரிகள்' },
      description: {
        en: 'Manage your delivery addresses',
        ta: 'உங்கள் முகவரிகளை நிர்வகிக்கவும்'
      },
      link: '/address'
    },
    {
      id: 'profile',
      icon: User,
      title: { en: 'Profile', ta: 'சுயவிவரம்' },
      description: {
        en: 'Edit your personal information',
        ta: 'உங்கள் தனிப்பட்ட தகவலைத் திருத்தவும்'
      },
      link: '/profile/edit'
    },
    {
      id: 'contact',
      icon: Phone,
      title: { en: 'Contact Us', ta: 'எங்களை தொடர்பு கொள்ள' },
      description: {
        en: 'Get support and help',
        ta: 'ஆதரவு மற்றும் உதவி பெறுங்கள்'
      },
      link: '/where-we-work'
    },
    {
      id: 'shop',
      icon: ShoppingBag,
      title: { en: 'Continue Shopping', ta: 'ஷாப்பிங் தொடரவும்' },
      description: {
        en: 'Browse our products',
        ta: 'எங்கள் பொருட்களைப் பாருங்கள்'
      },
      link: '/product'
    },
    {
      id: 'logout',
      icon: LogOut,
      title: { en: 'Logout', ta: 'வெளியேறு' },
      description: {
        en: 'Sign out from your account',
        ta: 'உங்கள் கணக்கிலிருந்து வெளியேறவும்'
      },
      action: () => {
        clearAuth();
        navigate('/login');
      }
    }
  ];

  const AccountCard = ({ icon: Icon, title, description, link, action }) => (
    <div 
      onClick={action || (() => navigate(link))}
      className="bg-white rounded-lg p-6 shadow hover:shadow-md transition-all duration-300 cursor-pointer border border-gray-100 hover:border-blue-100"
    >
      <div className="flex items-center space-x-4">
        <div className="bg-blue-50 p-3 rounded-full">
          <Icon className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            {title[isTamil ? 'ta' : 'en']}
          </h3>
          <p className="text-sm text-gray-500">
            {description[isTamil ? 'ta' : 'en']}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12 mt-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-8">
          <div className="flex items-center space-x-4">
            <div className="bg-blue-100 rounded-full p-4">
              <User className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isTamil ? 'வணக்கம், ' : 'Welcome, '}{storedUsername}
              </h1>
              <p className="text-gray-600 mt-1">
                {isTamil 
                  ? 'உங்கள் கணக்கை நிர்வகிக்கவும்'
                  : 'Manage your account'}
              </p>
            </div>
          </div>
        </div>

        {/* Account Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {accountCards.map((card) => (
            <AccountCard
              key={card.id}
              icon={card.icon}
              title={card.title}
              description={card.description}
              link={card.link}
              action={card.action}
            />
          ))}
        </div>

        {/* Help Text */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            {isTamil 
              ? 'உதவி தேவையா? எங்களை தொடர்பு கொள்ளவும்'
              : 'Need help? Contact us'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MyProfile;